/**
 * 送金完了メール送信モーダル（メーカー宛）。
 *
 * 経理「送金依頼」タブで選択したレコードをまとめ、メーカーへ送金完了報告メールを送る。
 * - 宛先 = 対応する製品カテゴリーの manufacturerEmail（prepare で解決）
 * - 本文 = 選択レコードの 顧客名(英語)/インボイス/商品/金額 を列挙（編集可）
 * - 送金証憑画像をアップロード → 自動添付 ＋ Storage 保存
 * - 送信成功で選択レコードを completed にマーク
 */
import { Loader2, Send, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { EmailChipsInput } from '@/components/common/EmailChipsInput';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import {
  usePrepareTransferCompletionMail,
  useCompleteTransferRequests,
  type TransferRequestRecord,
} from '@/hooks/useTransferRequests';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requests: TransferRequestRecord[];
  onCompleted: () => void;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      const idx = data.indexOf(',');
      resolve(idx >= 0 ? data.slice(idx + 1) : data);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function TransferCompletionDialog({
  open,
  onOpenChange,
  requests,
  onCompleted,
}: Props) {
  const { data: accounts } = useMailAccounts();
  const prepare = usePrepareTransferCompletionMail();
  const complete = useCompleteTransferRequests();

  const [loading, setLoading] = useState(false);
  const [toList, setToList] = useState<string[]>([]);
  const [fromAccountId, setFromAccountId] = useState('');
  const [ccList, setCcList] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeAccounts = (accounts ?? []).filter((a) => a.status === 'active');
  const requestIds = requests.map((r) => r.id);
  const idsKey = requestIds.join(',');

  const loadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || requestIds.length === 0) return;
    if (loadedRef.current === idsKey) return;
    loadedRef.current = idsKey;
    setLoading(true);
    prepare
      .mutateAsync({ requestIds })
      .then((r) => {
        setToList(r.defaultTo ?? []);
        setFromAccountId(r.fromAccountId ?? '');
        setSubject(r.subject ?? '');
        setBody(r.body ?? '');
        setCcList(r.defaultCc ?? []);
        setImage(null);
      })
      .catch((err) => {
        toast.error(`メール内容の取得に失敗しました: ${(err as Error).message}`);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey]);

  useEffect(() => {
    if (!open) loadedRef.current = null;
  }, [open]);

  const handlePickImage = (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('画像は 10MB 以下にしてください');
      return;
    }
    setImage(f);
  };

  const handleSend = async () => {
    if (toList.length === 0) {
      toast.error('宛先（メーカー）を入力してください');
      return;
    }
    try {
      let imagePayload:
        | { contentBase64: string; filename: string; contentType: string }
        | null = null;
      if (image) {
        const base64 = await readFileAsBase64(image);
        imagePayload = {
          contentBase64: base64,
          filename: image.name,
          contentType: image.type,
        };
      }
      const r = await complete.mutateAsync({
        requestIds,
        to: toList,
        cc: ccList,
        subject,
        body,
        fromAccountId: fromAccountId || null,
        image: imagePayload,
      });
      if (r.ok) {
        toast.success(`${r.completedCount} 件を送金完了しました`);
        onCompleted();
      } else {
        toast.error(`送信できませんでした: ${r.reason ?? '理由不明'}`);
      }
    } catch (err) {
      toast.error(`送信失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>送金完了メールを送信（メーカー宛・{requests.length}件）</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
            <Loader2 className="size-5 animate-spin mr-2" />
            メール内容を読み込み中…
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs">送信元</Label>
              <select
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
                className="flex h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-focus)] focus:outline-none"
              >
                {activeAccounts.length === 0 && (
                  <option value="">送信可能なアカウントがありません</option>
                )}
                {activeAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName ? `${a.displayName} <${a.email}>` : a.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs">宛先（メーカー）</Label>
              <EmailChipsInput
                value={toList}
                onChange={setToList}
                placeholder="manufacturer@example.com（Enter / カンマで複数）"
              />
              {toList.length === 0 && (
                <p className="text-[11px] text-[var(--accent-amber)] mt-1">
                  カテゴリー設定にメーカーメールが未登録です。宛先を直接入力してください。
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">CC</Label>
              <EmailChipsInput
                value={ccList}
                onChange={setCcList}
                placeholder="CCを追加（Enter / カンマ区切り）"
              />
            </div>

            <div>
              <Label className="text-xs">件名</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div>
              <Label className="text-xs">本文</Label>
              <Textarea
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-mono text-xs"
              />
            </div>

            {/* 証憑画像 */}
            <div>
              <Label className="text-xs">送金証憑画像（自動添付）</Label>
              {image ? (
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="truncate flex-1">
                    {image.name} ({(image.size / 1024).toFixed(0)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={() => setImage(null)}
                    className="text-[var(--text-muted)] hover:text-[var(--accent-red)]"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-1.5 px-3 h-8 mt-1 rounded-md border border-dashed border-[var(--border)] text-xs cursor-pointer hover:bg-[var(--bg-hover)]">
                  <Upload className="size-3.5" />
                  画像を選択（送金証憑）
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePickImage(e.target.files?.[0] ?? null)}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={complete.isPending}
          >
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={loading || complete.isPending || toList.length === 0}
          >
            <Send className="size-3.5" />
            {complete.isPending ? '送信中…' : '送金完了メールを送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
